import React, { FC, KeyboardEvent, ChangeEvent, useCallback, useMemo, useEffect } from 'react';
import _debounce from 'lodash/debounce';
import styled, { css } from 'styled-components';
import Icon from '@minecraft.icon';
import Input from '@minecraft.input';

const StyledInput = styled(Input)`
  padding-right: 2.25rem;
  border-radius: 0.25rem;
  height: 100%;
  width: 100%;

  &::placeholder {
    color: ${(props): string => props.theme.form.placeholderColor};
    font-size: 0.875rem;
  }
`;

const SearchIcon = styled(Icon)<{ name: string }>`
  color: ${(props): string => props.theme.typography.body.color};
  position: absolute;
  top: 50%;
  right: 0.625rem;
  ${({ name }) => css`
    height: ${name === 'close' ? '0.68rem' : '1rem'};
    width: ${name === 'close' ? '0.68rem' : '1rem'};
    cursor: ${name === 'close' ? 'pointer' : 'unset'};
  `}
  transform: translateY(-50%);
`;

const Search = styled.div`
  align-items: center;
  display: flex;
  position: relative;
  height: 2rem;
  width: 100%;
`;

export type Props = {
  debouncedOnChange?: boolean;
  debounceDelay?: number;
  name: string;
  value?: string;
  placeholder?: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onEnterPress?: () => void;
  onClear?: () => void;
  className?: string;
  testId?: string;
  label?: string;
};

const SearchInput: FC<Props> = ({
  debouncedOnChange = false,
  debounceDelay = 250,
  name,
  value,
  placeholder,
  onChange,
  onClear,
  onEnterPress,
  className,
  testId = 'search-input',
  label,
}) => {
  const iconValue = value ? 'close' : 'search';
  const handleEnterPress = useCallback((event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter' && onEnterPress) {
      onEnterPress();

      event.currentTarget.blur();
    }
  }, []);

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    onChange(event);
  }, []);

  const debouncedHandleChange = useMemo(() => _debounce(handleChange, debounceDelay), []);

  useEffect(() => {
    return () => {
      debouncedHandleChange.cancel();
    };
  }, []);

  return (
    <Search className={className}>
      <StyledInput
        name={name}
        value={value}
        placeholder={placeholder}
        onChange={debouncedOnChange ? debouncedHandleChange : handleChange}
        onKeyPress={handleEnterPress}
        data-qa-id="search-input"
        data-testid={testId}
        label={label}
      />
      <SearchIcon inheritColor name={iconValue} onClick={() => value && onClear?.()} />
    </Search>
  );
};

export default SearchInput;
